// Warn users about the risks of pasting commands in the browser console
console.warn(
	"%cWarning! NEVER paste anything here unless you fully understand what it does. Doing so can give attackers full access to your payment and personal data.",
	"color: black; background-color: yellow; font-size: 40px; font-weight: bold; padding: 10px; border: 5px solid red;",
);

window.global ||= window;

import "./log";
import React from "react";
import ReactDOM from "react-dom";
import { applyBrowserSupportClasses } from "./browserSupport";
import { handleLoadError } from "./reload";
import { createDocumentRoot } from "./root";

// Tag <html> with capability classes (no-flexbox-gap / no-focus-visible) so
// styles can provide fallbacks on older browsers. Runs before render.
applyBrowserSupportClasses();

// Force a reload when page is loaded from bfcache
window.onpageshow = (event) => {
	if (event.persisted) {
		window.location.reload();
	}
};

// Vite dispatches this when a dynamically-imported chunk fails to load
// (e.g. after a deploy invalidated the hashed asset). Route it through the
// same guarded auto-reload / "Reload Checkout" fallback as import() errors.
window.addEventListener("vite:preloadError", () => {
	handleLoadError({ message: "vite:preloadError" });
});

const scrollPolyfillPromise = () => {
	// Safari (2020-01-27), IE, Edge < 79, Chrome < 61, Firefox < 36
	// https://caniuse.com/#feat=scrollintoview&search=Int
	if (!("scrollBehavior" in document.documentElement.style)) {
		return import("smoothscroll-polyfill").then((smoothscroll) => {
			// enable the polyfill!
			smoothscroll.polyfill();
		});
	}
};

// Conditionally load core-js only when the browser lacks modern runtime APIs
// used by the app or its bundled dependencies. We probe the newest APIs in that
// set (ES2023: Array.prototype.toReversed/toSorted, ...); because browser
// feature support is monotonic per engine, a browser that has these has every
// older API too and needs nothing. Anything missing → fetch src/polyfills.ts.
// (Legacy/nomodule browsers already have core-js from @vitejs/plugin-legacy, so
// this check passes for them and no redundant chunk is fetched.)
const modernPolyfillsPromise = () => {
	const arrayProto = Array.prototype as unknown as {
		toReversed?: unknown;
		toSorted?: unknown;
		findLast?: unknown;
	};
	const objectCtor = Object as unknown as {
		fromEntries?: unknown;
		hasOwn?: unknown;
	};
	const hasModernApis =
		typeof arrayProto.toReversed === "function" &&
		typeof arrayProto.toSorted === "function" &&
		typeof arrayProto.findLast === "function" &&
		typeof objectCtor.fromEntries === "function" &&
		typeof objectCtor.hasOwn === "function" &&
		typeof Promise.allSettled === "function" &&
		typeof structuredClone === "function";
	return hasModernApis ? undefined : import("./polyfills");
};

const polyfills = () =>
	Promise.all([modernPolyfillsPromise(), scrollPolyfillPromise()]);
const importI18n = () => import("./i18n");
const importStartApp = () =>
	import("./App").then((app) => {
		const root = createDocumentRoot();
		const App = app.default;
		ReactDOM.render(<App />, root);
	});

// Finish one promise before starting the next
const promiseSequence = [polyfills, importI18n, importStartApp];
(async () => {
	for (const fn of promiseSequence) {
		try {
			await fn();
		} catch (e) {
			handleLoadError(e);
			// Rethrow so that we can log the error.
			throw e;
		}
	}
})();
